import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { groupBy, sum, pluck, sort } from 'ramda';
import { differenceInMilliseconds, format, parse } from 'date-fns';

import Header from './Header';
import CollapseButton from './CollapseButton'
import PieBlock from './PieBlock';
import BarChart from './BarChart';
import EditButton from './EditButton';
import CostsList from './CostsList';

import { colors } from '../constant';
import { respondTo } from '../responsive';

const ProjectItem = ({
    uuid, name, status, type, pm_name, creator_name, principal_name, client = '-', start_date, end_date, budget, 
    profit, average_person_cost, cost_sum,
    project_work_hours, project_costs, serial_number,
    created_at,
    viewMode
}) => {

    const [ isOpen, setOpen ] = useState(false);
    const ref = useRef(null);
    const afterToggleCollapse = useRef(false);

    useEffect(() => {
        if (!afterToggleCollapse.current) return;
        afterToggleCollapse.current = false;

        const height = ref.current.getBoundingClientRect().height;
        const isBigThenWindow = height > window.innerHeight;
        
        if ((isOpen && !isBigThenWindow) || !isOpen) {
            window.scrollTo({
                top: ref.current.offsetTop + 80 - (window.innerHeight - height) * 0.5,
                left: 0,
                behavior: 'smooth'
            });
        } else {
            window.scrollTo({
                top: ref.current.offsetTop + 64,
                left: 0,
                behavior: 'smooth'
            });
        }
    }, [isOpen])



    // Computed values

    const personData = useMemo(() => {
        if (!isOpen || !project_work_hours) return null;
        
        const groupedByName = groupBy(_ => _.user_name, project_work_hours);
        
        let rtn = Object.keys(groupedByName).map(name => ({
            name: name,
            used: sum(pluck('weighted_duration', groupedByName[name])),
            cost: sum(pluck('cost', groupedByName[name])) / 3.5,
        }));

        return rtn;

    }, [project_work_hours, isOpen]);

    const departmentData = useMemo(() => {
        if (!isOpen || !project_work_hours) return null;

        const groupedByDepartment = groupBy(_ => _.user_department, project_work_hours);
        
        let rtn = Object.keys(groupedByDepartment).map(name => ({
            name: name.replace('undefined', '-'),
            used: sum(pluck('weighted_duration', groupedByDepartment[name]))
        }));

        return rtn;
        
    }, [project_work_hours, isOpen])

    const projectDurationDate = useMemo(() => {
        let start = start_date;
        let end = end_date;

        if (!start || !end) {
            let dates = pluck('start_at', project_work_hours);
            dates = sort((a, b) => {
                return differenceInMilliseconds(
                    parse(a, 'yyyy-MM-dd', new Date()),
                    parse(b, 'yyyy-MM-dd', new Date())
                )
            }, dates);

            start = start || dates[0];
            end = end || dates[dates.length - 1];
        }

        return { start, end };
    }, [start_date, end_date]);

    const parseCreatedAt = useMemo(() => {
        try {
            return format(parse(created_at, 'yyyy-MM-dd HH:mm:ss', new Date()), 'yyyy-MM-dd')
        }
        catch (e) {
            let created_at_new = created_at.replace('T', ' ').replace('.000000Z', '');
            return format(parse(created_at_new, 'yyyy-MM-dd HH:mm:ss', new Date()), 'yyyy-MM-dd');
        }
    }, [created_at]);

    const canUseHour = useMemo(() => {
        return ((budget - cost_sum) * (1 - (profit || 0.3)) / (average_person_cost || 625)) || undefined;
    }, [budget, profit, average_person_cost]);

    const nowUsedHour = useMemo(() => {
        return ~~(sum(pluck('weighted_duration', project_work_hours)) / 60 / 60);
    }, [project_work_hours]);




    // Events

    function toggleCollapse() {
        setOpen(prev => !prev);
        afterToggleCollapse.current = true;
    }


    return (
        <RootLi ref={ref} isOpen={isOpen}>
            <Header name={name} serial_number={serial_number} status={status} type={type} pm_name={pm_name || '-'} owner={principal_name || creator_name || '-'} client={client} start_date={start_date || parseCreatedAt} end_date={end_date || '至今'} budget={budget}
                canUseHour={canUseHour} nowUsedHour={nowUsedHour} cost_sum={cost_sum}
                onClick={toggleCollapse}
            />
            <CollapseButton isOpen={isOpen} onClick={toggleCollapse} />
            
            { isOpen &&
                <DetailBlock>
                    <PieContainer>
                        { personData && <PieBlock label="成員時程佔比" data={personData} total={canUseHour * 60 * 60}/> }
                        <PieSplit/>
                        <PieBlock label="部門時程佔比" data={departmentData} total={0}/>
                    </PieContainer>
                    { (viewMode === 'real-cost') && 
                        <PieContainer>
                            <PieBlock label="成員成本佔比" data={personData} total={0} isCostPie/>
                        </PieContainer>
                    }
                    { project_costs && 
                        <CostsList project_costs={project_costs} cost_sum={cost_sum}/>
                    }
                    { project_work_hours && 
                        <BarChart 
                            startAt={start_date || projectDurationDate.start}
                            endAt={end_date || projectDurationDate.end}
                            data={project_work_hours}    
                        />
                    }
                    
                </DetailBlock>
            }
            { isOpen && <EditButton projectUuid={uuid}/> }
        </RootLi>
    )
}

const LiEnter = keyframes`
    0% {
        opacity: 0;
        transform: translateY(10%);
    }
    100% {
        opacity: 1;
        transform: translateY(0%);
    }
`

const animationDelay = index => `&:nth-child(${index}) { animation-delay: ${index * 0.05}s };`

const RootLi = styled.li`
    display: block;
    margin-bottom: 32px;
    background-color: ${ colors.bgDepth1 };
    box-shadow: 2px 4px 10px rgba(0, 0, 0, 0.08);

    box-sizing: border-box;
    padding: 16px 24px;
    padding-bottom: 20px;
    border-radius: 16px;

    position: relative;

    transition: box-shadow .2s, transform .5s;
    ${({ isOpen }) => isOpen && css`
        box-shadow: 2px 6px 14px rgba(0, 0, 0, 0.1);
        transform: translateY(-16px);
    `}

    opacity: 0;
    animation: ${LiEnter} .5s forwards ease;
    ${animationDelay(1)}
    ${animationDelay(2)}
    ${animationDelay(3)}
    ${animationDelay(4)}
    ${animationDelay(5)}
    ${animationDelay(6)}
    ${animationDelay(7)}
    ${animationDelay(8)}
    ${animationDelay(9)}
    ${animationDelay(10)}
    &:nth-child(n + 10) { animation-delay: ${10 * 0.05}s };

    ${ respondTo.md } {
        padding: 16px 8px;
        margin-bottom: 24px;
    }

    &:hover {
        box-shadow: 2px 8px 12px rgba(0, 0, 0, 0.14);
    }

`

const mobileSplit = css`
    &::before {
        content: ' ';
        display: block;
        width: 32px;
        height: 4px;
        margin: 0 auto;
        background-color: ${ colors.label1 };
        border-radius: 50em;
    }
    
`

const DetailBlock = styled.div`
    margin-bottom: 48px;
`

const PieContainer = styled.div`
    margin-top: 16px;
    padding: 40px 0;
    border-top: 1px solid #E0E0E0;
    position: relative;

    display: flex;
    align-items: stretch;

    ${ respondTo.lg } {
        flex-direction: column;
        border-top: 0;
        ${ mobileSplit }
        &::before {
            margin-bottom: 32px;
        }
    }

`

const PieSplit = styled.div`
    width: 1px;
    margin: 0 24px;
    background-color: #E0E0E0;

    ${ respondTo.lg } {
        width: 100%;
        margin: 32px 0;
        background-color: transparent;

        display: flex;
        justify-content: center;

        ${ mobileSplit }
    }

`


export default ProjectItem;
